import { Intent } from '@blueprintjs/core';
import { getPortalSdk } from 'portal-sdk';
import * as React from 'react';
import { isOnPremise, usePortalUser } from 'vet-bones/utils/portal';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { withConfirmation } from 'src/components/withConfirmation';
import { CURRENT_MIGRATION_VERSION } from 'src/reducers/migrations/constants';
import { Project } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { encodeCloudContent } from 'src/utils/exportProject/encodeProject';
import { getCloudProjectQueryKey } from 'src/utils/hooks/useCloudStorageProject';
import { getCloudProjectListQueryKey } from 'src/utils/hooks/useCloudStorageProjects';
import { useCloudStorageProjectsWithContent } from 'src/utils/hooks/useCloudStorageProjectsWithContent';
import { useNavigateProject } from 'src/utils/hooks/useNavigateProject';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { queryClient } from 'src/utils/queryClient';

let isDialogShowing = false;

const updateCloudProject = async (localProject: Project) => {
  const id = localProject.cloudProjectId || '';
  const portalSdk = getPortalSdk();
  const projectContents = encodeCloudContent(
    {
      ...localProject,
      id,
      cloudProjectId: undefined,
      cloudProjectLastUpdated: undefined,
      isCloudProject: true,
    },
    CURRENT_MIGRATION_VERSION
  );
  if (projectContents) {
    const updatedId = await portalSdk?.updateDesignerProject(
      id,
      projectContents,
      localProject.name,
      localProject.connectionId
    );
    queryClient.refetchQueries(getCloudProjectListQueryKey());
    if (updatedId) {
      queryClient.refetchQueries(getCloudProjectQueryKey(updatedId));
      return updatedId;
    }
  }
  return null;
};

export const useCloudStorageBackupProjects = () => {
  const { data: profileData } = usePortalUser();
  const { projectId: currentlyOpenProjectId } = useRouterParams();
  const dispatch = useReduxDispatch();
  const navigateToProject = useNavigateProject();
  const localCopiesOfCloudStorageProjects = useReduxSelector((state) =>
    (state.projects.projectIds || [])
      .map((id) => state.projects.projectsById[id])
      .filter((project) => !project.isRemoved && project.cloudProjectId)
  );

  const cloudProjectIds = localCopiesOfCloudStorageProjects.map(
    (project) => project.cloudProjectId || ''
  );

  const cloudProjectsWithContent =
    useCloudStorageProjectsWithContent(cloudProjectIds);

  const processedProjectsRef = React.useRef<string[]>([]);

  if (
    !profileData ||
    !profileData.is_designer_storage_enabled ||
    !cloudProjectIds.length ||
    !cloudProjectsWithContent.data
  ) {
    return;
  }

  cloudProjectsWithContent.data.forEach(async (cloudProject) => {
    if (!cloudProject) {
      return;
    }

    const localProject = localCopiesOfCloudStorageProjects.find(
      (singleProject) => singleProject.cloudProjectId === cloudProject?.id
    );

    if (localProject) {
      const localProjectUpdatedAt = localProject?.cloudProjectLastUpdated;
      const remoteProjectUpdatedAt = cloudProject?.dateLastUpdated;
      const isLocalProjectOutdated =
        localProjectUpdatedAt && remoteProjectUpdatedAt
          ? localProjectUpdatedAt < remoteProjectUpdatedAt
          : null;

      if (
        !isLocalProjectOutdated &&
        localProject.cloudProjectId === cloudProject?.id
      ) {
        try {
          const updatedId = await updateCloudProject(localProject);
          // if the current project is the one that was updated then we need to migrate it to the cloud
          if (
            updatedId &&
            !processedProjectsRef.current.includes(localProject.id)
          ) {
            processedProjectsRef.current.push(localProject.id);

            if (
              localProject.id === currentlyOpenProjectId ||
              currentlyOpenProjectId === cloudProject.id
            ) {
              navigateToProject(cloudProject.id);
            }
            dispatch(
              projectsActionCreators.migratedLocalProjectBackupToCloud(
                localProject.id,
                localProject.name
              )
            );
          }
        } catch (error) {
          // Project may still be too large for cloud storage.
          // Will retry on next render after user removes large files.
          console.warn('Failed to update cloud project:', error);
        }
      } else if (
        !isDialogShowing &&
        !processedProjectsRef.current.includes(localProject.id)
      ) {
        isDialogShowing = true;
        withConfirmation({
          title: copy.components.cloudStorage.outOfSyncDialog.title,
          message: copy.components.cloudStorage.outOfSyncDialog.message(
            localProject.name,
            remoteProjectUpdatedAt,
            localProjectUpdatedAt || 0,
            isOnPremise()
          ),
          isCloseButtonShown: false,
          yesPrompt: copy.components.cloudStorage.outOfSyncDialog.yesPrompt,
          yesButtonIntent: Intent.PRIMARY,
          noPrompt: copy.components.cloudStorage.outOfSyncDialog.noPrompt(
            isOnPremise()
          ),
          noButtonIntent: Intent.DANGER,

          onConfirmationSuccess: () => {
            dispatch(
              projectsActionCreators.duplicatedCloudBackupToLocalProject(
                localProject.id,
                localProject.name
              )
            );
            queryClient.refetchQueries(getCloudProjectListQueryKey());
            queryClient.refetchQueries(
              getCloudProjectQueryKey(cloudProject.id)
            );
            isDialogShowing = false;
          },

          onConfirmationFailure: async () => {
            processedProjectsRef.current.push(localProject.id);
            try {
              const updatedId = await updateCloudProject(localProject);
              // if the current project is the one that was updated then we need to migrate it to the cloud
              if (updatedId) {
                await queryClient.refetchQueries(getCloudProjectListQueryKey());
                await queryClient.refetchQueries(
                  getCloudProjectQueryKey(updatedId)
                );
                dispatch(
                  projectsActionCreators.migratedLocalProjectBackupToCloud(
                    localProject.id,
                    localProject.name
                  )
                );
                if (
                  localProject.id === currentlyOpenProjectId ||
                  currentlyOpenProjectId === cloudProject.id
                ) {
                  navigateToProject(cloudProject.id);
                }
              }
            } catch (error) {
              // Project may still be too large for cloud storage.
              console.warn('Failed to update cloud project:', error);
            }
            isDialogShowing = false;
          },
        });
      }
    }
  });
};
